class BotController {
  constructor(gameInterface, botId, gameState) {
    this.gameInterface = gameInterface;
    this.botId = botId;
    this.gameState = gameState;
    this.updateInterval = setInterval(() => this.update(), 100);
    
    this.HUNGER_THRESHOLD = 40;
    this.COLD_THRESHOLD = 40;
    this.RESOURCE_GATHER_RANGE = 60;
    this.FIRE_RANGE = 100;
    this.BOT_AVOIDANCE_RANGE = 80;
  }

  getDistance(x1, y1, x2, y2) {
    return Math.sqrt(Math.pow(x2 - x1, 2) + Math.pow(y2 - y1, 2));
  }

  findNearestResource(type) {
    const bot = this.gameState.entities[this.botId];
    if (!bot) return null;

    let nearest = null;
    let minDistance = Infinity;

    Object.values(this.gameState.entities).forEach(entity => {
      if (entity.type === type && 
          ((type === 'bush' && entity.fruits > 0) || 
           (type === 'tree' && entity.logs > 0))) {
        const distance = this.getDistance(bot.x, bot.y, entity.x, entity.y);
        if (distance < minDistance) {
          minDistance = distance;
          nearest = entity;
        }
      }
    });

    return nearest;
  }

  isNearFire() {
    const bot = this.gameState.entities[this.botId];
    if (!bot) return false;

    return Object.values(this.gameState.entities).some(entity => 
      entity.type === 'fire' && 
      this.getDistance(bot.x, bot.y, entity.x, entity.y) < this.FIRE_RANGE
    );
  }

  getAvoidanceVector() {
    const bot = this.gameState.entities[this.botId];
    let avoidX = 0;
    let avoidY = 0;

    Object.values(this.gameState.entities).forEach(entity => {
      if (entity.type === 'player' && entity.id !== this.botId) {
        const distance = this.getDistance(bot.x, bot.y, entity.x, entity.y);
        if (distance < this.BOT_AVOIDANCE_RANGE) {
          const force = (this.BOT_AVOIDANCE_RANGE - distance) / this.BOT_AVOIDANCE_RANGE;
          avoidX += (bot.x - entity.x) * force;
          avoidY += (bot.y - entity.y) * force;
        }
      }
    });

    return { avoidX, avoidY };
  }

  moveTowards(targetX, targetY) {
    const bot = this.gameState.entities[this.botId];
    if (!bot) return;

    const distance = this.getDistance(bot.x, bot.y, targetX, targetY);
    
    // Stop when in range to trigger automatic gathering
    if (distance > this.RESOURCE_GATHER_RANGE) {
      const { avoidX, avoidY } = this.getAvoidanceVector();
      
      const dx = (targetX - bot.x) + avoidX;
      const dy = (targetY - bot.y) + avoidY;
      const length = Math.sqrt(dx * dx + dy * dy);
      
      this.gameInterface.sendGameEvent('joystick_move', {
        userId: this.botId,
        dx: dx / length,
        dy: dy / length
      });
    } else {
      // Stop moving to allow automatic gathering to trigger
      this.gameInterface.sendGameEvent('joystick_move', {
        userId: this.botId,
        dx: 0,
        dy: 0
      });
    }
  }

  update() {
    const bot = this.gameState.entities[this.botId];
    if (!bot || bot.isDead) return;

    // Don't move while gathering
    if (bot.gatheringTimer > 0) {
      this.gameInterface.sendGameEvent('joystick_move', {
        userId: this.botId,
        dx: 0,
        dy: 0
      });
      return;
    }

    // Handle eating and fire building (still manual actions)
    if (bot.hunger < this.HUNGER_THRESHOLD && bot.fruits > 0) {
      this.gameInterface.sendGameEvent('eat', { userId: this.botId });
    }

    if (bot.cold < this.COLD_THRESHOLD && bot.logs >= 5 && !this.isNearFire()) {
      this.gameInterface.sendGameEvent('build_fire', { userId: this.botId });
    }

    // Find and move to resources
    let targetResource = null;
    if (bot.hunger < bot.cold || bot.fruits === 0) {
      targetResource = this.findNearestResource('bush');
    } else if (bot.cold < bot.hunger || bot.logs < 5) {
      targetResource = this.findNearestResource('tree');
    } else {
      targetResource = bot.fruits < bot.logs ? 
        this.findNearestResource('bush') : 
        this.findNearestResource('tree');
    }

    if (targetResource) {
      this.moveTowards(targetResource.x, targetResource.y);
    } else {
      // Explore randomly if no resources found
      const randomX = Math.random() * this.gameState.worldSize.width;
      const randomY = Math.random() * this.gameState.worldSize.height;
      this.moveTowards(randomX, randomY);
    }
  }

  destroy() {
    clearInterval(this.updateInterval);
  }
}

export default BotController;
